import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { getAccountPlan, saveAccountPlan } from 'Shared/util/web-api';
import Sidebar from 'Shared/components/sidebar';
import InputText from 'Shared/components/input-text';
import Button from 'Shared/components/button';
import { useStatusMessage, useTranslation } from 'Shared/util/custom-hooks';
import StatusMessage from 'Shared/components/status-message';
import Spinner from 'Shared/components/spinner';
import styles from './accountplan-edit-view.css';


const AccountPlanEditView = ({ show, hide, setHasAccountPlan }) => {
  const [accountPlan, setAccountPlan] = useState([]);
  const [inputStates, setInputStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sameValuesExist, setSameValuesExist] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, dispatchStatusMessage] = useStatusMessage();
  const t = useTranslation(x => x.accountPlan);
  const tg = useTranslation(x => x.global);
  const sidebarRef = useRef();

  const scrollSidebarTop = () => {
    if (sidebarRef && sidebarRef.current) {
      sidebarRef.current.scrollTop = 0;
    }
  };

  const initialCheck = (array) => {
    const stateArray = [];
    const create = array[0].id === 0;
    const valuesArray = array.map((item) => {
      stateArray.push(true);
      return create ? item.accountCategory.standardPlan : item.ugAccount;
    });
    setInputStates(stateArray);
    return valuesArray.some((item, index) => valuesArray.indexOf(item) !== index);
  };

  useEffect(() => {
    if (show) {
      dispatchStatusMessage({ type: 'NONE' });
      getAccountPlan()
        .then((response) => {
          setAccountPlan(response);
          setSameValuesExist(initialCheck(response));
          setLoading(false);
        });
    } else {
      setAccountPlan([]);
      setInputStates([]);
      setLoading(true);
    }
  }, [show]);

  const checkSameValue = (value) => accountPlan.filter((plan) => {
    const compareValue = plan.ugAccount
      ? plan.ugAccount
      : plan.accountCategory.standardPlan;
    return compareValue === value;
  }).length;

  const handleInputChange = (e) => {
    const { name, value } = e.currentTarget;

    if (value.match('^[0-9]{0,4}$') != null) {
      const index = name.split('-')[1];
      const newAccountPlan = [...accountPlan];
      newAccountPlan[index].ugAccount = value;
      setAccountPlan(newAccountPlan);

      if (value.length === 4) setSameValuesExist(checkSameValue(value) > 1);
    }
  };

  const handleFormSubmit = () => {
    setSubmitting(true);
    dispatchStatusMessage({ type: 'NONE' });

    saveAccountPlan(accountPlan)
      .then(() => {
        setSubmitting(false);
        scrollSidebarTop();
        dispatchStatusMessage({
          type: 'SUCCEEDED',
          message: t.chartOfAccountsSaved,
        });

        setTimeout(() => {
          hide();
          if (setHasAccountPlan) setHasAccountPlan(true);
        }, 1500);
      })
      .catch(() => {
        scrollSidebarTop();
        dispatchStatusMessage({
          type: 'FAILED',
          message: t.couldNotSaveChartOfAccounts,
        });
      });
  };

  const setValidState = (bool, index) => {
    const arr = [...inputStates];
    arr[index] = bool;
    setInputStates(arr);
  };

  const isFormInValid = () => inputStates.filter((x) => x === false).length > 0 || sameValuesExist;

  return (
    <Sidebar
      ref={sidebarRef}
      show={show}
      onClickOutside={hide}
      className={styles.sidebar}
    >
      <h3>{t.chartOfAccounts}</h3>
      {loading
        ? <Spinner />
        : (
          <>
            <StatusMessage
              status={statusMessage.type}
              text={statusMessage.message}
              clearStatus={() => dispatchStatusMessage({ type: 'NONE' })}
              className={styles.statusMessageContainer}
            />
            {accountPlan.map((plan, index) => (
              <InputText
                key={`${plan.id}:${plan.accountCategory.categoryName}`}
                setEnabledSubmit={(bool) => setValidState(bool, index)}
                isRequired
                label={t[plan.accountCategory.categoryIdTranslationTag]}
                inputType="account-category"
                name={`accountplan-${index}`}
                value={plan.ugAccount !== null ? plan.ugAccount : plan.accountCategory.standardPlan}
                onChange={handleInputChange}
                compareFunc={checkSameValue}
              />
            ))}
            <div className={styles.buttonGroup}>
              <Button
                text={tg.close}
                buttonType="button-secondary"
                onClick={() => hide()}
              />
              <Button
                buttonType="button-primary"
                text={tg.saveAndClose}
                onClick={() => handleFormSubmit()}
                isLoading={submitting}
                attributes={{ disabled: isFormInValid() }}
              />
            </div>
          </>
        )}
    </Sidebar>
  );
};

AccountPlanEditView.propTypes = {
  show: PropTypes.bool.isRequired,
  hide: PropTypes.func.isRequired,
  setHasAccountPlan: PropTypes.func,
};

export default AccountPlanEditView;
